/**
 * PhysicalCheckoutModal — VARIANT 1 (physical) of the 3-variant checkout.
 *
 * 3-step flow: details (quantity + fulfillment method ship/pickup +
 * address/pickup confirm) → summary → 2FA → create.
 *
 * SPEC-244 · partición VERBATIM de
 * frontend/src/components/market/checkout-modal.tsx (2.637 L).
 *
 * El monolito original mezclaba estado + handlers + render en un único
 * componente de ~1.247 L. Esta vista fina se compone de:
 *   - `usePhysicalCheckout` hook (toda la lógica de estado + handlers).
 *   - `PhysicalDetailsStep` (Step 1 — quantity, variation, fulfillment,
 *     ship form, pickup confirm).
 *   - `PhysicalSummaryStep` (Step 2 — order summary + escrow note).
 *   - `PhysicalVerifyStep` (Step 3 — 2FA-verified banner + create CTA).
 *   - `StepIndicator` + `TwoFaCheckoutDialog` (shared).
 *
 * Behavior preserved VERBATIM from Fase 4 — only the wrapper extracts the
 * 3 step bodies into sub-components (T-PROPS-PASSING) + state into a
 * hook (T-HOOK-EXTRACTION).
 */

'use client';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ShoppingBag } from 'lucide-react';
import type { CheckoutModalProps } from '../types';
import { usePhysicalCheckout } from '../hooks/use-physical-checkout';
import { StepIndicator } from './step-indicator';
import { PhysicalDetailsStep } from './physical-details-step';
import { PhysicalSummaryStep } from './physical-summary-step';
import { PhysicalVerifyStep } from './physical-verify-step';

// ─── Component ───────────────────────────────────────────────────────────────

export function PhysicalCheckoutModal({
  open,
  onClose,
  product,
  sellerFulfillment,
}: CheckoutModalProps) {
  const checkout = usePhysicalCheckout({ open, onClose, product, sellerFulfillment });
  const {
    step,
    setStep,
    quantity,
    setQuantity,
    fulfillmentMethod,
    setFulfillmentMethod,
    shippingAddress,
    setShippingAddress,
    savedAddresses,
    savedAddressesLoading,
    selectedSavedAddressId,
    handleSelectSavedAddress,
    indicaciones,
    setIndicaciones,
    saveThisAddress,
    setSaveThisAddress,
    addressLabel,
    setAddressLabel,
    pickupConfirmed,
    setPickupConfirmed,
    variationSelection,
    setVariationSelection,
    submitting,
    submitError,
    setSubmitError,
    effectiveShips,
    effectivePickup,
    variation,
    hasVariations,
    detailsValid,
    estimate,
    pickupInfo,
    pickupLoc,
    mapsLink,
    callCreate,
    handleOpenChange,
  } = checkout;

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          className="sm:max-w-lg max-h-[90vh] overflow-y-auto"
          aria-describedby="checkout-modal-desc"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingBag className="size-5" aria-hidden="true" />
              {step === 'details' && 'Checkout — Detalles de envío'}
              {step === 'summary' && 'Checkout — Resumen de la orden'}
              {step === 'verify' && 'Checkout — Confirmar compra'}
            </DialogTitle>
            <DialogDescription id="checkout-modal-desc">
              {step === 'details' &&
                'Selecciona la cantidad y el método de entrega. Pagarás con tus créditos de Profileia Market.'}
              {step === 'summary' &&
                'Revisa los detalles de tu orden antes de continuar.'}
              {step === 'verify' &&
                'Confirma para crear la orden y debitar tus créditos al escrow.'}
            </DialogDescription>
          </DialogHeader>

          {/* Step indicator (1·2·3) — visual progress, no interaction. */}
          <StepIndicator step={step} firstLabel="Detalles" />

          {/* ── Step 1: Details ── */}
          {step === 'details' && (
            <PhysicalDetailsStep
              product={product}
              effectiveShips={effectiveShips}
              effectivePickup={effectivePickup}
              quantity={quantity}
              setQuantity={setQuantity}
              fulfillmentMethod={fulfillmentMethod}
              setFulfillmentMethod={setFulfillmentMethod}
              variation={variation}
              hasVariations={hasVariations}
              variationSelection={variationSelection}
              setVariationSelection={setVariationSelection}
              shippingAddress={shippingAddress}
              setShippingAddress={setShippingAddress}
              savedAddresses={savedAddresses}
              savedAddressesLoading={savedAddressesLoading}
              selectedSavedAddressId={selectedSavedAddressId}
              handleSelectSavedAddress={handleSelectSavedAddress}
              indicaciones={indicaciones}
              setIndicaciones={setIndicaciones}
              saveThisAddress={saveThisAddress}
              setSaveThisAddress={setSaveThisAddress}
              addressLabel={addressLabel}
              setAddressLabel={setAddressLabel}
              pickupConfirmed={pickupConfirmed}
              setPickupConfirmed={setPickupConfirmed}
              pickupInfo={pickupInfo}
              pickupLoc={pickupLoc}
              mapsLink={mapsLink}
              detailsValid={detailsValid}
              submitError={submitError}
              onClose={onClose}
              onContinue={() => {
                setSubmitError(null);
                setStep('summary');
              }}
            />
          )}

          {/* ── Step 2: Summary ── */}
          {step === 'summary' && (
            <PhysicalSummaryStep
              product={product}
              variationSelection={variationSelection}
              quantity={quantity}
              fulfillmentMethod={fulfillmentMethod}
              shippingAddress={shippingAddress}
              estimate={estimate}
              pickupLoc={pickupLoc}
              submitting={submitting}
              submitError={submitError}
              onBack={() => setStep('details')}
              onContinue={() => setStep('verify')}
              onClose={onClose}
            />
          )}

          {/* ── Step 3: Verify (after 2FA) ── */}
          {step === 'verify' && (
            <PhysicalVerifyStep
              estimate={estimate}
              submitting={submitting}
              submitError={submitError}
              onBack={() => setStep('summary')}
              onCreate={() => void callCreate()}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
